import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useGetZipCodes, useValidateZipCodes } from 'entities/campaign';
import { Button } from 'shared/ui/Button';
import { AlertModal, SideModal, SideModalBody, SideModalFooter, useModalsActions } from 'shared/ui/SideModal';
import { Textarea } from 'shared/ui/Textarea';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { form } from 'shared/lib/form';

import { useZipsActions, useZipsValue } from './UseZipsStore';
import { ZipsResultsModalCampaign } from './ZipsResultsModalCampaign';

interface ZipsEditorModalProps {
  campaignId: number;
}

export function ZipsEditorModal({ campaignId }: ZipsEditorModalProps) {
  const inputZipsCodes = useZipsValue();
  const getZipCodesQuery = useGetZipCodes(campaignId);
  const campaignZipCodes = getZipCodesQuery.data?.data.zipCodes.join(', ');

  return (
    <SideModal isLoading={getZipCodesQuery.isFetching}>
      {campaignZipCodes && (
        <EditorBody
          campaignId={campaignId}
          campaignZipCodes={campaignZipCodes}
          inputZipCodes={inputZipsCodes}
        />
      )}
    </SideModal>
  );
}

interface EditorBodyProps extends ZipsEditorModalProps {
  campaignZipCodes: string;
  inputZipCodes: string;
}

function EditorBody({ campaignId, inputZipCodes, campaignZipCodes }: EditorBodyProps) {
  const zipCodes = inputZipCodes || campaignZipCodes;
  const { open } = useModalsActions();
  const validateZipCodesQuery = useValidateZipCodes(campaignId);
  const { setZipsValue } = useZipsActions();
  const methods = useForm({ defaultValues: { zipCodes } });
  const formValue = useWatch({ control: methods.control, name: 'zipCodes' });

  const submit = methods.handleSubmit((params) => {
    validateZipCodesQuery.mutate(
      { payload: params },
      {
        onSuccess: ({ data: { zipCodes: validationResponse } }) => {
          setZipsValue(formValue);
          const areZipsChanged = formValue.trim() !== campaignZipCodes && !validationResponse.invalidCount;

          open(
            <ZipsResultsModalCampaign
              campaignId={campaignId}
              zipValidationData={{
                ...validationResponse,
                nextBillingStartAt: areZipsChanged ? validationResponse.nextBillingStartAt : null,
              }}
            />
          );
        },
      }
    );
  });

  return (
    <SideModal autoTestAttributesName="edit-zips-modal">
      <FormProvider {...methods}>
        <SideModalBody title="Edit ZIPs">
          <AlertModal
            autoTestAttributesName="edit-zips-exit-modal"
            showCondition={form(methods)}
          />
          <div className="mb-4 text-sm">Each ZIP Code can be placed on a new line or separated by a comma.</div>
          <form
            onSubmit={submit}
            className="flex flex-1 flex-col justify-between pb-4"
          >
            <Textarea
              isDisabled={validateZipCodesQuery.isLoading}
              className="h-screen flex-1"
              name="zipCodes"
            />
          </form>
        </SideModalBody>
        <SideModalFooter>
          <Button
            {...getDataAutoTestAttributes(['button-check-zips'])}
            onClick={submit}
            loading={validateZipCodesQuery.isLoading}
            className="w-full"
            disabled={!formValue.trim()}
          >
            Check ZIPs
          </Button>
        </SideModalFooter>
      </FormProvider>
    </SideModal>
  );
}
